import {
    Component,
    ElementRef,
    OnInit,
    AfterViewInit,
    OnDestroy,
    ViewChild,
    ChangeDetectorRef,
    ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { finalize, map } from 'rxjs';
import { CommonModule, DatePipe } from '@angular/common';
import { SafeResourceUrl } from '@angular/platform-browser';
import { AdditionalInformationComponent } from '../../../my-profile/additional-information/additional-information.component';
import { OverviewComponent } from '../../../my-profile/overview/overview.component';
import { WelcomeComponent } from '../../../my-profile/welcome/welcome.component';
import { PlantsService } from '../services/plants.service';
import { TimestampDatePipe } from '../shared/timestamp-date.pipe';
import { InstallationDatePipe } from '../shared/installation-date.pipe';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { WelcomeLoaderComponent } from '../../../common/welcome-loader/welcome-loader.component';
import * as L from 'leaflet';
import { MatDialogModule } from '@angular/material/dialog';
import { ExternalReference } from '../reference-ids/reference-ids.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

type PlantInfo = {
    id: number;
    plantOwner: string;
    cityId: number;
    userName: string;
    email: string;
    phoneNumber: string;
    latitude?: string;
    longitude?: string;
    plantImage?: string;
    Address?: string;
    disabled?: boolean;
    CreatedAt?: string;
    lastUpdate?: string;
    plantId?: number;
    lowPriceThreshold?: string;
    priceClass?: string;
    customerType?: string;
    userId?: number;
    externalReferences?: ExternalReference[];
    cityName?: string;
    installationDate?: string;
};

type PlantFullResponse = {
    inverters?: any[];
    plantInfo?: PlantInfo;
};

@Component({
    selector: 'app-plants-detail',
    imports: [
        RouterLink,
        WelcomeComponent,
        AdditionalInformationComponent,
        OverviewComponent,
        MatCardModule,
        MatIconModule,
        CommonModule,
        TimestampDatePipe,
        InstallationDatePipe,
        MatChipsModule,
        MatListModule,
        WelcomeLoaderComponent,
        MatDialogModule,
        MatTooltipModule,
        TranslatePipe,
        MatSlideToggleModule
    ],
    templateUrl: './plants-detail.component.html',
    styleUrls: ['./plants-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DatePipe]
})
export class PlantsDetailComponent implements OnInit, AfterViewInit, OnDestroy {
    plantId!: number;
    plantsDetail: any;
    mapUrl: SafeResourceUrl | null = null;
    inverterDetail: any[] = [];
    plantInfo: PlantInfo | null = null;
    loading: boolean = true;
    externalReferences: ExternalReference[] = [];
    @ViewChild('map', { static: false }) mapContainer?: ElementRef<
        HTMLDivElement
    >;
    private map?: L.Map;
    private marker?: L.Marker;
    private viewInitialized = false;

    iconRetinaUrl = '/images/icons/marker-icon-blue.png';
    iconUrl = '/images/icons/marker-icon-blue.png';
    shadowUrl = '/images/icons/marker-shadow.png';
    private leafletIconConfigured = false;

    constructor(
        private plantService: PlantsService,
        private route: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private sb: MatSnackBar,
        private translate: TranslateService
    ) { }

    ngOnInit(): void {
        this.configureLeafletDefaultIcon();
        this.route.paramMap.subscribe(params => {
            const idStr = params.get('id');
            if (!idStr) {
                return;
            }
            this.plantId = +idStr;
            this.loadPlant();
        });
    }

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        this.initOrUpdateMap();
    }

    get hasCoordinates(): boolean {
        return !!(this.plantInfo?.latitude && this.plantInfo?.longitude);
    }

    loadPlant() {
        this.loading = true;
        this.plantService
            .getPlantFullDetail(this.plantId)
            .pipe(
                map(
                    (res: any): PlantFullResponse => {
                        const inverters = Array.isArray(res?.inverters)
                            ? res.inverters
                            : [];
                        const plantInfo = res?.plantInfo ?? null;
                        return { inverters, plantInfo };
                    }
                )
            )
            .subscribe({
                next: (mapped: PlantFullResponse) => {                    
                    this.inverterDetail = mapped.inverters ?? [];
                    this.plantInfo = mapped.plantInfo ?? null;
                    this.plantsDetail = {
                        inverters: this.inverterDetail,
                        plantInfo: this.plantInfo
                    };

                    this.externalReferences =
                        (this.plantInfo as any)?.externalReferences ?? [];

                    this.loading = false;

                    this.cdr.detectChanges();
                    setTimeout(() => {
                        this.initOrUpdateMap();
                    }, 0);
                },
                error: (err: any) => {
                    console.error('Failed to load plant - error object:', err);
                    this.inverterDetail = [];
                    this.plantInfo = null;
                    this.loading = false;
                }
            });
    }

    private async initOrUpdateMap(): Promise<void> {
        if (!this.viewInitialized) {
            return;
        }

        if (!this.hasCoordinates || !this.mapContainer) {
            return;
        }

        await this.waitForMapContainerSize();

        const lat = Number(this.plantInfo!.latitude);
        const lng = Number(this.plantInfo!.longitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            console.warn('Invalid coordinates', { lat, lng });
            return;
        }

        const center: L.LatLngExpression = [lat, lng];

        if (!this.map) {
            this.map = L.map(this.mapContainer.nativeElement, {
                center,
                zoom: 14
            });

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '© OpenStreetMap contributors',
                crossOrigin: 'anonymous'
            }).addTo(this.map);
        } else {
            this.map.setView(center, 14);
        }

        if (this.marker) {
            this.marker.setLatLng(center);
        } else {
            this.marker = L.marker(center).addTo(this.map);
        }
        setTimeout(() => {
            this.map?.invalidateSize(true);
        }, 150);
    }

    getBatteryProgress(capacity: string, max = 50) {
        const val = parseFloat(capacity);
        if (isNaN(val)) return 0;
        return Math.min(val / max, 1);
    }

    goToUpdatePlant() {
        const plantIdFromRoute = this.route.snapshot.paramMap.get('id');
        const plantId =
            this.plantInfo?.id ??
            (plantIdFromRoute ? Number(plantIdFromRoute) : null);

        if (!plantId) {
            console.warn('No plant id resolved for update');
            return;
        }

        this.router.navigate(['/plants/update', plantId]);
    }

    goToActivityReports(inv: any) {
        const serialNumber = inv?.serialNumber;
        const userId = this.plantInfo?.userId;
        const plantId = this.plantId;

        if (!serialNumber || !userId) {
            console.warn(
                'Missing serialNumber or userId for activity reports navigation',
                {
                    serialNumber,
                    userId
                }
            );
            return;
        }

        this.router
            .navigate(['/plants/activity-reports'], {
                queryParams: {
                    userId,
                    serialNumber,
                    plantId
                },
                state: {
                    totalBatteryCapacityKwh: inv?.totalBatteryCapacityKwh,
                    inverterId: inv?.id
                }
            })
            .then(success => {
                if (!success) {
                    console.error('Navigation to activity reports failed');
                }
            });
    }

    goToEditInverter(inv: any) {
        const serialNumber = inv?.serialNumber ?? inv?._serialNumber;
        const plantId = this.plantId;

        if (!serialNumber || !plantId) {
            console.warn('Missing serialNumber or plantId for edit inverter', {
                serialNumber,
                plantId
            });
            return;
        }

        this.router
            .navigate(['/plants/plants', plantId, 'inverters', 'update'], {
                queryParams: {
                    inverterSerial: serialNumber
                }
            })
            .then(success => {
                if (!success)
                    console.error('Navigation to update inverter failed');
            });
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
        }
    }

    onAddInverter() {
        if (!this.plantId) return;
        this.router.navigate(['/plants', this.plantId, 'inverters', 'add']);
    }

    private waitForMapContainerSize(): Promise<void> {
        return new Promise(resolve => {
            const el = this.mapContainer?.nativeElement;
            if (!el) return resolve();

            const check = () => {
                const rect = el.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) return resolve();
                requestAnimationFrame(check);
            };

            check();
        });
    }

    private configureLeafletDefaultIcon(): void {
        if (this.leafletIconConfigured) return;

        delete (L.Icon.Default.prototype as any)._getIconUrl;

        L.Icon.Default.mergeOptions({
            iconRetinaUrl: this.iconRetinaUrl,
            iconUrl: this.iconUrl,
            shadowUrl: this.shadowUrl
        });

        this.leafletIconConfigured = true;
    }

    hasSolarFactorThreshold(inv: any): boolean {
        return this.getSolarFactorThresholdValues(inv).length > 0;
    }

    formatSolarFactorThreshold(inv: any): string {
        return this.getSolarFactorThresholdValues(inv)
            .map(value => {
                if (value == null) return '';

                if (typeof value === 'object') {
                    return String(
                        value.label ??
                        value.name ??
                        value.value ??
                        value.threshold ??
                        ''
                    );
                }

                return String(value);
            })
            .filter(value => value.trim() !== '')
            .join(' • ');
    }

    private getSolarFactorThresholdValues(inv: any): any[] {
        const raw =
            inv?.solarFactorThreshold ?? inv?._solarFactorThreshold ?? [];

        if (raw == null || raw === '') {
            return [];
        }

        return Array.isArray(raw) ? raw : [raw];
    }

    isInverterDisabled(inv: any): boolean {
        return !!(inv?.disabled ?? inv?._disabled);
    }

    isInverterToggleBusy(inv: any): boolean {
        return !!inv?._toggleLoading;
    }

    onToggleInverter(inv: any, checked: boolean): void {
        const inverterId = Number(inv?.id ?? inv?._id);
        const plantId = Number(this.plantId);

        if (!plantId || !inverterId || this.isInverterToggleBusy(inv)) {
            return;
        }

        const previousDisabled = this.isInverterDisabled(inv);
        const nextDisabled = !checked;

        inv._toggleLoading = true;

        // optimistic update
        inv.disabled = nextDisabled;
        inv._disabled = nextDisabled;

        this.plantService
            .toggleDisableInverter(plantId, inverterId)
            .pipe(
                finalize(() => {
                    inv._toggleLoading = false;
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: (res: any) => {
                    const serverDisabled = res?.disabled ?? res?._disabled;
                    const finalDisabled =
                        typeof serverDisabled === 'boolean'
                            ? serverDisabled
                            : nextDisabled;

                    inv.disabled = finalDisabled;
                    inv._disabled = finalDisabled;

                    this.notify(
                        finalDisabled
                            ? this.translate.instant(
                                'plants.inverter.toggle.disabledSuccess'
                            )
                            : this.translate.instant(
                                'plants.inverter.toggle.enabledSuccess'
                            ),
                        'success'
                    );
                },
                error: (err: any) => {
                    inv.disabled = previousDisabled;
                    inv._disabled = previousDisabled;

                    this.notify(
                        err?.statusMessage ||
                        err?.message ||
                        this.translate.instant(
                            'plants.inverter.toggle.error'
                        ),
                        'error'
                    );
                }
            });
    }

    private notify(
        msg: string,
        type: 'success' | 'error' | 'warn' | 'info' = 'info',
        duration = 4500
    ) {
        const cls =
            type === 'success'
                ? 'snack-success'
                : type === 'error'
                    ? 'snack-error'
                    : type === 'warn'
                        ? 'snack-warn'
                        : 'snack-info';

        this.sb.open(msg, 'Close', {
            duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: [cls]
        });
    }
}
